

/* global google */
import { useEffect, useRef } from 'react';
import { handleRotationChange, getCurrentRotation } from '../helpers/rotationUtils.js';


const DraggableOverlay = ({
  map,
  url,
  bounds,
  onBoundsChanged,
  onDragEnd,
  isShiftDraggingRef,
  overlayIsMirrored
}) => {


  const overlayRef = useRef(null);


  useEffect(() => {

    if (!map) return;

    class CustomOverlay extends google.maps.OverlayView {
      constructor(bounds, image) {
        super();
        this.bounds_ = bounds;
        this.image_ = image;
        this.div_ = null;
        this.dragging_ = false;
        this.rotating_ = false;
        this.startPos_ = null;
        this.startBounds_ = null;
        this.hasMoved_ = false;
        this.rotationAngle_ = getCurrentRotation();
      }


      // You do not directly call onAdd or onRemove. The Google Maps API calls them automatically at the appropriate times.
      //onAdd is called only once per overlay instance when it is added to the map. It is not called repeatedly during panning,
      // zooming, or other map interactions. If your onAdd method is being called more than once for a single overlay, it
      // indicates that the overlay is being recreated and re-added to the map, which is usually a sign of a problem in your
      // component's rendering logic. The draw method is responsible for redrawing the overlay during map interactions.
      onAdd() {
        // console.log('CustomOverlay onAdd called');
        const div = document.createElement('div');
        div.style.borderStyle = 'none';
        div.style.borderWidth = '0px';
        div.style.position = 'absolute';
        div.style.touchAction = 'none';
        div.style.pointerEvents = 'auto';
        div.style.webkitUserSelect = 'none';  // Prevent Safari interpreting touches as selection events.
        div.style.webkitTouchCallout = 'none';  // Prevent Safari interpreting touches as selection events.

        const img = document.createElement('img');
        img.src = this.image_;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.position = 'absolute';
        img.style.cursor = 'move';
        img.style.touchAction = 'none';
        img.style.pointerEvents = 'none';
        img.style.userSelect = 'none';  // Prevent Safari interpreting touches as selection events.
        img.style.webkitTouchCallout = 'none';  // Prevent Safari interpreting touches as selection events.
        div.style.zIndex = '100';

        div.appendChild(img);
        this.div_ = div;

        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(div);

        this.boundOnDrag = this.onDrag.bind(this);
        this.boundStopDrag = this.stopDrag.bind(this);

        // Add listeners for mouse events and touch.
        div.addEventListener('mousedown', this.startDrag.bind(this), { passive: false });
        div.addEventListener('touchstart', this.startDrag.bind(this), { passive: false });
      }

      //The draw method is separate from onAdd and onRemove. draw is called whenever the map needs to redraw the overlay,
      // for example, after a zoom, pan, or resize. Use draw to update the position and appearance of your overlay on the
      // map, not to initialize or clean up.
      draw() {

        //console.log('CustomOverlay draw called');
        const overlayProjection = this.getProjection();
        if (!overlayProjection) {
          //console.log('CustomOverlay draw: overlayProjection is null');
          return;
        }

        const sw = overlayProjection.fromLatLngToDivPixel(this.bounds_.getSouthWest());
        const ne = overlayProjection.fromLatLngToDivPixel(this.bounds_.getNorthEast());

        const div = this.div_;
        if (div) {
          div.style.left = sw.x + 'px';
          div.style.top = ne.y + 'px';
          div.style.width = (ne.x - sw.x) + 'px';
          div.style.height = (sw.y - ne.y) + 'px';

          const scale = overlayIsMirrored ? -1 : 1;
          div.style.transform = `rotate(${this.rotationAngle_}deg) scaleX(${scale})`;
        }
      }

      onRemove() {
        // console.log('CustomOverlay onRemove called');
        if (this.div_) {
          this.div_.parentNode.removeChild(this.div_);
          this.div_ = null;
          //console.log('CustomOverlay onRemove: div removed');
        }
      }

      startDrag(e) {

        if (map) {
          map.setOptions({ gestureHandling: 'none' });
        }

        e.preventDefault();
        e.stopPropagation();

        this.dragging_ = true;
        this.rotating_ = e.shiftKey;  // Use Shift key to rotate

        if (this.rotating_ && isShiftDraggingRef) {
          isShiftDraggingRef.current = true;
        }

        const isTouch = e.type.startsWith('touch');

        const point = isTouch ? e.touches[0] : e;
        this.startPos_ = { x: point.clientX, y: point.clientY };

        this.startBounds_ = this.bounds_;

        // Add listeners for mouse events and touch.
        document.addEventListener(isTouch ? 'touchmove' : 'mousemove', this.boundOnDrag, { passive: false });
        document.addEventListener(isTouch ? 'touchend' : 'mouseup', this.boundStopDrag, { passive: false });
      }

      onDrag(e) {

        if (!this.dragging_) return;
        //console.log('CustomOverlay onDrag called with event:', e);

        const point = e.type.startsWith('touch') ? e.touches[0] : e;

        e.stopPropagation();

        const overlayProjection = this.getProjection();
        if (!overlayProjection) {
          return;
        }

        const dx = point.clientX - this.startPos_.x;
        const dy = point.clientY - this.startPos_.y;

        if (!this.hasMoved_) {
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 2) {
            this.hasMoved_ = true;
          } else {
            return; // Don't start moving yet
          }
        }

        if (this.rotating_) {
          const ROTATION_SENSITIVITY = 0.02; // Smaller = slower rotation
          this.rotationAngle_ += dx * ROTATION_SENSITIVITY;
          this.draw();
        }
        else {
          const sw = overlayProjection.fromLatLngToDivPixel(this.startBounds_.getSouthWest());
          const ne = overlayProjection.fromLatLngToDivPixel(this.startBounds_.getNorthEast());

          const newSW = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(sw.x + dx, sw.y + dy));
          const newNE = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(ne.x + dx, ne.y + dy));

          this.bounds_ = new google.maps.LatLngBounds(newSW, newNE);
          this.draw();

          onBoundsChanged({
            north: newNE.lat(),
            south: newSW.lat(),
            east: newNE.lng(),
            west: newSW.lng(),
          });
        }
      }

      stopDrag(e) {

        if (map) {
          map.setOptions({ gestureHandling: 'auto' });
        }

        this.dragging_ = false;
        this.rotating_ = false;

        if (isShiftDraggingRef) {
          isShiftDraggingRef.current = false;
        }

        document.removeEventListener('mousemove', this.boundOnDrag);
        document.removeEventListener('mouseup', this.boundStopDrag);
        document.removeEventListener('touchmove', this.boundOnDrag);
        document.removeEventListener('touchend', this.boundStopDrag);

        if (this.hasMoved_) {
          onDragEnd();
        }

        this.hasMoved_ = false;

        // Update the rotation state in parent component
        handleRotationChange(this.rotationAngle_); // Finalise + write to URL
      }
    }
    //console.log('Creating overlay with bounds:', bounds, 'and URL:', url, 'and rotation:', rotation);
    const overlayBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(bounds.south, bounds.west),
      new google.maps.LatLng(bounds.north, bounds.east),
    );

    const customOverlay = new CustomOverlay(overlayBounds, url);
    customOverlay.setMap(map);
    overlayRef.current = customOverlay;

    return () => {
      //console.log('Cleaning up overlay and listeners');
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
    };

  }, [map, url, bounds, onBoundsChanged, onDragEnd, isShiftDraggingRef, overlayIsMirrored]);

  return null;
};


export default DraggableOverlay;


